/**
 * Theme Extraction Utilities
 *
 * Reusable functions for extracting configurations from theme documents.
 * Can be used for fonts, shadows, border-radius, spacing, etc.
 */

import { findBlockByKey } from './document.utils';

/**
 * Extracts field values from a section
 * Returns an object with field keys mapped to their values
 */
export function extractFieldsFromSection(section: any): Record<string, any> {
  if (!section || !section.fields) return {};

  const fieldValues: Record<string, any> = {};

  section.fields.forEach((field: any) => {
    if (field.key && field.value !== undefined) {
      fieldValues[field.key] = field.value;
    }
  });

  return fieldValues;
}

/**
 * Generic function to extract configurations from a theme block
 *
 * @param theme - The theme document
 * @param blockKey - The key of the block to extract (e.g., 'text-styles', 'shadows', 'border-radius')
 * @param sectionMapping - Mapping of section keys to output keys
 * @param validator - Optional function to validate if extracted config is valid
 * @returns Object with extracted configurations
 *
 * @example
 * // Extract fonts
 * const fonts = extractThemeBlock(theme, 'text-styles', {
 *   'heading-1': 'h1',
 *   'heading-2': 'h2'
 * }, (config) => config.fontName && config.size);
 *
 * // Extract shadows
 * const shadows = extractThemeBlock(theme, 'shadows', {
 *   'shadow-sm': 'sm',
 *   'shadow-md': 'md'
 * });
 */
export function extractThemeBlock<T extends Record<string, any>>(
  theme: any,
  blockKey: string,
  sectionMapping: Record<string, string>,
  validator?: (config: any) => boolean
): T {
  const result: any = {};

  try {
    const block = findBlockByKey(theme, blockKey);

    if (!block || !block.sections) return result as T;

    block.sections.forEach((section: any) => {
      const outputKey = sectionMapping[section.key];
      if (!outputKey) return;

      const fields = extractFieldsFromSection(section);

      // Apply validator if provided
      if (validator && !validator(fields)) return;

      result[outputKey] = fields;
    });

    return result as T;
  } catch (error) {
    return result as T;
  }
}
